import { useMemo, useState } from "react";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { Star, TrashSimple } from "phosphor-react";
import { TrackableData } from "../Trackables/trackablesData";

const idsMatch = (a, b) => a != null && b != null && Number(a) === Number(b);

const FilterLists = ({
  filterLists = [],
  trackableId,
  appliedFilterIds = [],
  selectedFilterId = null,
  onSelectFilter,
  onDeleteFilter,
  loggedInUser,
}) => {
  const [filterName, setFilterName] = useState("");
  const sortedFilterLists = useMemo(() => {
    return [...filterLists].sort((a, b) => {
      const aMatch = Number(a.trackable) === Number(trackableId);
      const bMatch = Number(b.trackable) === Number(trackableId);
      return Number(bMatch) - Number(aMatch);
    });
  }, [filterLists, trackableId]);

  const isFilterApplied = (filterId) =>
    appliedFilterIds.some((id) => idsMatch(id, filterId));

  const handleSelectFilter = (filter) => {
    onSelectFilter?.(filter);
  };

  const handleActionClick = async (event, filter) => {
    event.stopPropagation();
    if (!filter?.id) return;
    await onDeleteFilter?.(filter);
  };

  return (
    <>
      {filterLists.length > 0 ? (
        <>
          <TextField
            size="small"
            fullWidth
            placeholder="Search filters by name"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            sx={{
              mb: "12px",
              "& .MuiOutlinedInput-root": {
                bgcolor: "#252A38",
                color: "#F9F9F9",
                borderRadius: "5px",
                "& fieldset": { borderColor: "#606479" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                "&.Mui-focused fieldset": { borderColor: "#6C65D9" },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#606479",
                opacity: 1,
              },
            }}
          />
          {sortedFilterLists
            .filter((filter) =>
              filter.name.toLowerCase().includes(filterName.toLowerCase()),
            )
            .map((filter) => {
              const isApplied = isFilterApplied(filter.id);
              const isSelected = idsMatch(selectedFilterId, filter.id);

              return (
                <Box
                  key={filter.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectFilter(filter)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelectFilter(filter);
                    }
                  }}
                  sx={{
                    width: "100%",
                    backgroundColor: isApplied ? "#2f3550" : "#252A38",
                    borderRadius: "10px",
                    p: "10px",
                    mb: "10px",
                    cursor: "pointer",
                    border: isApplied
                      ? "1px solid #6C65D9"
                      : isSelected
                        ? "1px solid rgba(108, 101, 217, 0.55)"
                        : "1px solid transparent",
                    outline:
                      isSelected && !isApplied
                        ? "1px solid rgba(108, 101, 217, 0.35)"
                        : "none",
                    "&:hover": { backgroundColor: "#2f3550" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: "8px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#F9F9F9",
                      }}
                    >
                      {
                        TrackableData.find(
                          (trackable) => trackable.id === filter.trackable,
                        )?.icon
                      }
                      {filter.name}
                    </Typography>
                    <Box>
                      <IconButton
                        disabled
                        sx={{ padding: "2px 5px" }}
                        aria-label="Favorite filter"
                        // onClick={handleActionClick}
                      >
                        <Star size={18} color="#606479" />
                      </IconButton>
                      {String(filter?.created_by?.id) ===
                        String(loggedInUser?.id) && (
                        <IconButton
                          sx={{
                            padding: "2px 5px",
                            "&:hover": { bgcolor: "rgba(214, 31, 31, 0.12)" },
                          }}
                          aria-label="Delete filter"
                          onClick={(event) => handleActionClick(event, filter)}
                        >
                          <TrashSimple size={18} color="#D61F1F" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: "8px",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#B1B5C9" }}>
                      {filter.filters?.length ?? 0} condition
                      {(filter.filters?.length ?? 0) === 1 ? "" : "s"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isApplied
                          ? "#6C65D9"
                          : isSelected
                            ? "#B1B5C9"
                            : "#606479",
                      }}
                    >
                      {filter.created_by.first_name}{" "}
                      {filter.created_by.last_name}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
        </>
      ) : (
        <Typography variant="body2" sx={{ color: "#606479", py: 2 }}>
          Saved filters will appear here. You can create one in Filter Settings.
        </Typography>
      )}
    </>
  );
};

export default FilterLists;
